import React, { useState, ChangeEvent, FC, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Backdrop, Fade, IconButton, FormControl, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import styles from './MobilePrinterModal.css';
import Button from '../../buttons/Button';
import { mergeClassNames } from '../../../utils';
import RadioButton from '../../buttons/RadioButton';
import MobilePrinterContent from '../../pages/OrderList/MobilePrinter/MobilePrinterContent';
import { useMobilePrinter } from '../../../hooks/useMobilePrinter';

type Props = {
  isOpenDialog: boolean;
  onModalClose: () => void;
};

const useStyles = makeStyles({
  mobilePrinterRoot: {
    width: '100%',
  },
});

const MobilePrinterModal: FC<Props> = ({ isOpenDialog, onModalClose }) => {
  const [printerChoice, setPrinterChoice] = useState<'yes' | 'no' | undefined>();

  const { t } = useTranslation();
  const translationRoot = 'MOBILE_PRINTER';
  const classes = useStyles();
  const hasFetchedInModalRef = useRef(false);
  const shouldFetchList = printerChoice === 'yes' && !hasFetchedInModalRef.current;

  const {
    shouldDisableConnectButton,
    manualPrinterNumber,
    isPrinterConnected,
    connectedPrinterNumber,
    mobilePrinterList,
    mobilePrinterListError,
    mobilePrinterListLoading,
    selectedPrinterFromList,
    handleManualPrinterNumberChange,
    handlePrinterSelectionFromList,
    connectPrinter,
    skipPrinterConnection,
    resetToLocalStorageState,
  } = useMobilePrinter(shouldFetchList);

  const isSubmitDisabled = !printerChoice || (printerChoice === 'yes' && shouldDisableConnectButton);

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'yes' | 'no';
    setPrinterChoice(value);
  };

  const resetFields = useCallback(() => {
    setPrinterChoice(undefined);
    onModalClose();
  }, [onModalClose]);

  const handleSubmit = () => {
    try {
      if (printerChoice === 'yes') {
        connectPrinter();
      } else {
        skipPrinterConnection();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in handleSubmit:', error);
    }
    resetFields();
  };

  const handleCancel = () => {
    skipPrinterConnection();
    resetFields();
  };

  useEffect(() => {
    if (isOpenDialog) {
      // Sync hook state with localStorage when modal opens in case printer state changed externally
      // Ex: Mobile Printer page
      resetToLocalStorageState();
      hasFetchedInModalRef.current = false;
    }
  }, [isOpenDialog, resetToLocalStorageState]);

  useEffect(() => {
    if (shouldFetchList) {
      hasFetchedInModalRef.current = true;
    }
  }, [shouldFetchList]);

  return (
    <Modal
      open={isOpenDialog}
      onClose={onModalClose}
      className={styles.modalRoot}
      closeAfterTransition
      disableBackdropClick
      disableEscapeKeyDown
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
      aria-labelledby="mobile-printer-title"
      aria-describedby="mobile-printer-description"
      style={{ zIndex: 90 }} // overwrite default zIndex of 1300 to display error toast on top of modal
    >
      <Fade in={isOpenDialog}>
        <div className={styles.modalPaper} role="dialog" aria-modal="true">
          <div className={styles.modalHeader}>
            <h2 className={styles.modalHeading} id="mobile-printer-title">
              {t(`${translationRoot}.MOBILE_PRINTER_HEADING`)}
            </h2>
            <IconButton onClick={handleCancel} className={styles.closeButton} aria-label="Close modal">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={styles.modalContent} id="mobile-printer-description">
            <FormControl component="fieldset" className={styles.radioGroup}>
              <div role="radiogroup" aria-label="Printer usage selection">
                <RadioButton
                  id="printer-no"
                  name="printer-choice"
                  value="no"
                  label={t(`${translationRoot}.DEFAULT_PRINTER`)}
                  checked={printerChoice === 'no'}
                  onChange={handleRadioChange}
                  className={styles.customRadio}
                />
                <RadioButton
                  id="printer-yes"
                  name="printer-choice"
                  value="yes"
                  label={t('YES')}
                  checked={printerChoice === 'yes'}
                  onChange={handleRadioChange}
                  className={styles.customRadio}
                />
              </div>
            </FormControl>

            {printerChoice === 'yes' && (
              <div className={styles.inputSection}>
                <MobilePrinterContent
                  isPrinterConnected={isPrinterConnected}
                  manualPrinterNumber={manualPrinterNumber}
                  handleManualPrinterNumberChange={handleManualPrinterNumberChange}
                  translationRoot={translationRoot}
                  inputRootClass={classes.mobilePrinterRoot}
                  mobilePrinterListError={mobilePrinterListError}
                  mobilePrinterList={mobilePrinterList}
                  mobilePrinterListLoading={mobilePrinterListLoading}
                  handlePrinterSelectionFromList={handlePrinterSelectionFromList}
                  selectedPrinterFromList={selectedPrinterFromList}
                  connectedPrinterNumber={connectedPrinterNumber}
                />
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <Button
              className={mergeClassNames(styles.btn, styles.cancelButton)}
              data-cy="cancel-button"
              aria-label={t(`${translationRoot}.CANCEL`)}
              onClick={handleCancel}
            >
              {t(`${translationRoot}.CANCEL`)}
            </Button>
            <Button
              className={mergeClassNames(styles.btn, styles.submitButton)}
              data-cy="submit-button"
              disabled={isSubmitDisabled}
              aria-label={t(`${translationRoot}.SUBMIT`)}
              onClick={handleSubmit}
            >
              {t(`${translationRoot}.SUBMIT`)}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default MobilePrinterModal;
